import { LabelBox, LabelElement, LabelLayout } from "./layout";
import { LabelData } from "./types";

const PT_TO_MM = 25.4 / 72;

// Keep name and SKU as separate editable template elements, while adapting
// their rendered boxes to each variant's actual text. The name box keeps the
// template's position and width (manual resizing must stick) and shrinks to
// the text's height; the SKU follows directly below it and grows in height
// when its text wraps.
export const resolveNameSkuBoxes = (
  layout: LabelLayout,
  item: LabelData,
  widthMm: number,
  heightMm: number,
  // Rendered name text height (percent of label), see useMeasuredNameHeight.
  measuredNameHeightPercent?: number,
) => {
  const name = layout.elements.find(element => element.kind === "name");
  const sku = layout.elements.find(element => element.kind === "sku");

  if (!name || !sku) {
    return new Map<string, LabelBox>();
  }

  // The template height is the name's maximum — LabelFieldContent shrinks
  // longer names to fit it — but the box hugs shorter text, so the SKU sits
  // right under the last line instead of after a blank gap. It never grows
  // past the template: that used to push the name over neighbouring fields
  // (e.g. the QR code) placed right below it.
  const nameHeightPercent =
    measuredNameHeightPercent === undefined
      ? name.box.height
      : Math.min(name.box.height, measuredNameHeightPercent);
  const gapPercent = (1 * 100) / heightMm;
  const skuText = `${sku.showLabel ? "SKU: " : ""}${sku.content ?? item.sku ?? ""}`;
  const skuFont = sku.style?.fontSizePt ?? 9;
  const skuTextWidthMm = skuText.length * skuFont * PT_TO_MM * 0.72 + 0.5;
  const maxSkuWidthMm = Math.max(1, (sku.box.width / 100) * widthMm);
  const skuLines = Math.max(1, Math.ceil(skuTextWidthMm / maxSkuWidthMm));
  const skuHeightPercent = (skuLines * skuFont * PT_TO_MM * 1.25 * 100) / heightMm;
  const skuBox: LabelBox = {
    ...sku.box,
    x: name.box.x,
    y: Math.min(100 - skuHeightPercent, name.box.y + nameHeightPercent + gapPercent),
    width: sku.box.width,
    height: Math.max(sku.box.height, skuHeightPercent),
  };
  const boxes = new Map<string, LabelBox>([
    [name.id, { ...name.box, height: nameHeightPercent }],
    [sku.id, skuBox],
  ]);
  const qrcode = layout.elements.find(element => element.kind === "qrcode");
  const barcode = layout.elements.find(element => element.kind === "barcode");

  if (qrcode && barcode) {
    const qrBox = qrcode.box;
    const overlaps =
      skuBox.x < qrBox.x + qrBox.width &&
      skuBox.x + skuBox.width > qrBox.x &&
      skuBox.y < qrBox.y + qrBox.height &&
      skuBox.y + skuBox.height > qrBox.y;

    if (overlaps) {
      const requiredShift = skuBox.y + skuBox.height + gapPercent - qrBox.y;
      const maxShift = Math.min(
        100 - qrBox.y - qrBox.height,
        100 - barcode.box.y - barcode.box.height,
      );
      const shift = Math.max(0, Math.min(requiredShift, maxShift));

      boxes.set(qrcode.id, { ...qrBox, y: qrBox.y + shift });
      boxes.set(barcode.id, { ...barcode.box, y: barcode.box.y + shift });
    }
  }

  return boxes;
};

// The name's font is fitted to its template box, not to the measured box
// that hugs the text — otherwise measuring and fitting would feed back into
// each other.
export const contentElement = (element: LabelElement, renderElement: LabelElement) =>
  element.kind === "name" ? element : renderElement;
